import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import deLocale from '@fullcalendar/core/locales/de'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format, isAfter, parseISO, startOfToday } from 'date-fns'
import { toast } from 'sonner'
import {
  AlertCircleIcon,
  CalendarIcon,
  ListIcon,
  UsersIcon,
  ClockIcon,
  FileTextIcon,
  UserPlusIcon,
  MegaphoneIcon
} from 'lucide-react'
import { SessionMessageDialog } from '@/components/session-message-dialog'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { MobileCalendarToolbar } from '@/components/mobile-calendar-toolbar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  getConfirmedBookingCountsBySession,
  getCurrentUserRole,
  getSessionBookings,
  getTrainerSessions,
  getTrainerProfiles,
  updateSessionReport,
} from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import {
  TRAINER_CALENDAR_DEFAULT_SCROLL_TIME,
  TRAINER_CALENDAR_END_TIME,
  TRAINER_CALENDAR_SLOT_DURATION,
  TRAINER_CALENDAR_START_TIME,
} from '@/lib/constants'
import {
  MOBILE_CALENDAR_BREAKPOINT,
  getCalendarToolbarTitle,
  getInitialCalendarWindowWidth,
  getResponsiveCalendarLayout,
} from '@/lib/calendar-layout'
import { queryKeys } from '@/lib/query-keys'
import type { Booking, CourseSession, Profile } from '@/types/domain'

import './trainer-my-courses-page.css'

export function TrainerMyCoursesPage() {
  const queryClient = useQueryClient()
  const calendarRef = useRef<FullCalendar | null>(null)
  const [windowWidth, setWindowWidth] = useState(getInitialCalendarWindowWidth)
  const { calendarView, visibleDays } = useMemo(
    () => getResponsiveCalendarLayout(windowWidth),
    [windowWidth],
  )
  const [currentCalendarView, setCurrentCalendarView] = useState(calendarView)
  const [calendarTitle, setCalendarTitle] = useState('')
  const [view, setView] = useState<'list' | 'calendar'>('list')
  const [selectedSession, setSelectedSession] = useState<CourseSession | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false)

  // Reporting Form State
  const [reportMinutes, setReportMinutes] = useState<string>('')
  const [reportNotes, setReportNotes] = useState<string>('')
  const [reportSubstituteId, setReportSubstituteId] = useState<string>('none')

  const profileQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
  })

  const trainersQuery = useQuery({
    queryKey: ['trainers-all'],
    queryFn: getTrainerProfiles,
  })

  const trainerId = profileQuery.data?.id

  const [visibleRange, setVisibleRange] = useState(() => ({
    from: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
    to: new Date(Date.now() + 1000 * 60 * 60 * 24 * 7).toISOString(),
  }))

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setCurrentCalendarView(calendarView)
  }, [calendarView])

  // List view sessions (wide range)
  const listSessionsQuery = useQuery({
    queryKey: ['trainer-sessions-list', trainerId],
    queryFn: () =>
      getTrainerSessions(trainerId!, {
        from: new Date(Date.now() - 1000 * 60 * 60 * 24 * 90).toISOString(),
        to: new Date(Date.now() + 1000 * 60 * 60 * 24 * 180).toISOString(),
      }),
    enabled: !!trainerId,
  })

  // Calendar view sessions (dynamic/visible range)
  const calendarSessionsQuery = useQuery({
    queryKey: ['trainer-sessions-calendar', trainerId, visibleRange.from, visibleRange.to],
    queryFn: () => getTrainerSessions(trainerId!, visibleRange),
    enabled: !!trainerId,
    placeholderData: keepPreviousData,
  })

  const sessionsQuery = view === 'calendar' ? calendarSessionsQuery : listSessionsQuery

  const bookingsQuery = useQuery({
    queryKey: queryKeys.sessionBookings(selectedSession?.id ?? ''),
    queryFn: () => getSessionBookings(selectedSession!.id),
    enabled: !!selectedSession,
  })
  const confirmedBookings = useMemo(
    () =>
      (bookingsQuery.data ?? [])
        .filter((booking) => booking.status === 'confirmed')
        .sort(
          (a, b) =>
            a.full_name.localeCompare(b.full_name, 'de', { sensitivity: 'base' }) ||
            a.email.localeCompare(b.email, 'de', { sensitivity: 'base' }),
        ),
    [bookingsQuery.data],
  )

  const bookingCountsQuery = useQuery({
    queryKey: queryKeys.sessionBookingCounts,
    queryFn: getConfirmedBookingCountsBySession,
  })

  const reportMutation = useMutation({
    mutationFn: ({ sessionId, ...payload }: {
      sessionId: string
      actual_duration_minutes: number
      trainer_notes: string
      substitute_trainer_id: string | null
    }) => updateSessionReport(sessionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainer-sessions-list'] })
      queryClient.invalidateQueries({ queryKey: ['trainer-sessions-calendar'] })
      toast.success('Bericht erfolgreich gespeichert')
      setIsDialogOpen(false)
    },
    onError: (error) => {
      console.error('Failed to save report:', error)
      toast.error('Fehler beim Speichern des Berichts')
    },
  })

  const sessions = useMemo(() => sessionsQuery.data ?? [], [sessionsQuery.data])
  const bookingCounts = useMemo(() => bookingCountsQuery.data ?? {}, [bookingCountsQuery.data])
  const showMobileToolbar = windowWidth < MOBILE_CALENDAR_BREAKPOINT
  const calendarViews = [
    { label: 'Tag', value: 'timeGridDay' },
    { label: 'Woche', value: 'timeGridWeek' },
  ]

  const navigateCalendar = (direction: 'prev' | 'next') => {
    const calendarApi = calendarRef.current?.getApi()
    if (!calendarApi) return
    if (direction === 'next') {
      calendarApi.next()
      return
    }
    calendarApi.prev()
  }

  const goToToday = () => {
    calendarRef.current?.getApi().today()
  }

  const changeCalendarView = (nextView: string) => {
    calendarRef.current?.getApi().changeView(nextView)
  }

  const { upcomingSessions, pastSessions } = useMemo(() => {
    const today = startOfToday()
    const upcoming: CourseSession[] = []
    const past: CourseSession[] = []

    sessions.forEach((s) => {
      if (isAfter(parseISO(s.starts_at), today)) {
        upcoming.push(s)
      } else {
        past.push(s)
      }
    })

    return {
      upcomingSessions: upcoming.sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
      pastSessions: past.sort((a, b) => b.starts_at.localeCompare(a.starts_at)), // Latest first for past
    }
  }, [sessions])

  const calendarEvents = useMemo(
    () =>
      sessions.map((session) => {
        const booked = bookingCounts[session.id] ?? 0
        const isCancelled = session.is_cancelled
        const isPast = !isAfter(parseISO(session.starts_at), startOfToday())

        let color = session.courses?.color ?? '#38BDF8'
        if (isCancelled) color = '#ef4444'
        if (isPast && !isCancelled) color = '#94a3b8' // Gray for past

        return {
          id: session.id,
          title: session.courses?.name ?? 'Kurs',
          start: session.starts_at,
          end: session.ends_at,
          backgroundColor: color,
          borderColor: color,
          className: isPast ? 'opacity-70' : '',
          extendedProps: {
            booked,
            max: session.max_participants,
            min: session.min_participants,
            isCancelled,
            isPast,
            resourceName: session.resource?.name,
          },
        }
      }),
    [sessions, bookingCounts],
  )

  const handleSessionClick = (session: CourseSession) => {
    setSelectedSession(session)
    setReportMinutes(
      session.report?.actual_duration_minutes.toString() ??
      session.courses?.duration_minutes.toString() ??
      '',
    )
    setReportNotes(session.report?.trainer_notes ?? '')
    setReportSubstituteId(session.report?.substitute_trainer_id ?? 'none')
    setIsDialogOpen(true)
  }

  const handleSaveReport = () => {
    if (!selectedSession) return

    const minutes = parseInt(reportMinutes)
    if (isNaN(minutes)) {
      toast.error('Bitte gültige Minuten eingeben')
      return
    }

    reportMutation.mutate({
      sessionId: selectedSession.id,
      actual_duration_minutes: minutes,
      trainer_notes: reportNotes,
      substitute_trainer_id: reportSubstituteId === 'none' ? null : reportSubstituteId,
    })
  }

  if (profileQuery.isLoading || (view === 'list' && listSessionsQuery.isLoading)) {
    return (
      <div className="container mx-auto py-8">
        <div className="space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-[500px] w-full" />
        </div>
      </div>
    )
  }

  const renderSessionsTable = (
    sessionsList: CourseSession[],
    emptyMessage: string,
  ) => {
    if (sessionsList.length === 0) {
      return (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </p>
      )
    }

    return (
      <Table containerClassName="max-h-[60dvh] lg:max-h-full">
        <TableHeader>
          <TableRow>
            <TableHead>Datum & Zeit</TableHead>
            <TableHead>Kurs</TableHead>
            <TableHead>Ort</TableHead>
            <TableHead>Teilnehmer</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Aktionen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessionsList.map((session) => {
            const booked = bookingCounts[session.id] ?? 0
            const min = session.min_participants ?? 0
            const isReached = booked >= min
            const isCancelled = session.is_cancelled
            const isPast = !isAfter(parseISO(session.starts_at), startOfToday())
            const hasReport = !!session.report

            return (
              <TableRow
                key={session.id}
                className={`cursor-pointer transition-colors hover:bg-muted/50 ${isCancelled || isPast ? 'opacity-70' : ''}`}
                onClick={() => handleSessionClick(session)}
              >
                <TableCell className="font-medium whitespace-nowrap">
                  <div className="flex flex-col">
                    <span>{formatDateTime(session.starts_at)}</span>
                    <span className="text-[10px] text-muted-foreground font-normal">
                      {format(parseISO(session.starts_at), 'HH:mm')} -{' '}
                      {format(parseISO(session.ends_at), 'HH:mm')} ({session.courses?.duration_minutes}m)
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: isPast && !isCancelled ? '#94a3b8' : session.courses?.color }}
                    />
                    {session.courses?.name}
                  </div>
                </TableCell>
                <TableCell className="text-sm font-medium">
                  {session.resource?.name ?? '-'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 text-sm">
                    <UsersIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    <span>
                      {booked} / {session.max_participants}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {isCancelled ? (
                    <Badge variant="destructive">Storniert</Badge>
                  ) : isPast ? (
                    <div className="flex flex-col gap-1">
                      <Badge variant="secondary">Abgeschlossen</Badge>
                      {hasReport && (
                        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50 text-[9px] h-4">
                          Bericht OK
                        </Badge>
                      )}
                    </div>
                  ) : min === 0 || isReached ? (
                    <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                      Findet statt
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Wartet auf Teilnehmer</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm">
                    {isPast && !hasReport ? 'Bericht' : 'Details'}
                  </Button>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="mb-6 flex shrink-0 items-center justify-between">
        <h1 className="text-3xl font-bold">Meine Kurse</h1>
        <Tabs
          value={view}
          onValueChange={(v: string) => setView(v as 'list' | 'calendar')}
          className="w-auto"
        >
          <TabsList className="h-9 w-[200px] grid grid-cols-2">
            <TabsTrigger value="list" className="flex items-center gap-2">
              <ListIcon className="h-4 w-4" />
              Liste
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              Kalender
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Tabs value={view} className="flex min-h-0 flex-1 flex-col">
        <TabsContent
          className="mt-0 min-h-0 flex-1 space-y-4 lg:grid lg:grid-rows-2 lg:gap-4 lg:space-y-0"
          value="list"
        >
          <Card className="lg:flex lg:min-h-0 lg:flex-col">
            <CardHeader>
              <CardTitle>Kommende Kurse</CardTitle>
            </CardHeader>
            <CardContent className="lg:min-h-0 lg:flex-1 lg:overflow-hidden">
              {renderSessionsTable(upcomingSessions, 'Keine kommenden Kurse gefunden.')}
            </CardContent>
          </Card>

          <Card className="lg:flex lg:min-h-0 lg:flex-col">
            <CardHeader>
              <CardTitle>Vergangene Kurse (letzte 90 Tage)</CardTitle>
            </CardHeader>
            <CardContent className="lg:min-h-0 lg:flex-1 lg:overflow-hidden">
              {renderSessionsTable(pastSessions, 'Keine vergangenen Kurse gefunden.')}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent className="mt-0 min-h-0 flex-1" value="calendar">
          <Card className="flex h-full min-h-0 flex-col overflow-hidden">
            <CardContent className="min-h-0 flex-1 overflow-hidden p-4">
              {showMobileToolbar && (
                <MobileCalendarToolbar
                  currentView={currentCalendarView}
                  title={calendarTitle}
                  views={calendarViews}
                  onPrev={() => navigateCalendar('prev')}
                  onNext={() => navigateCalendar('next')}
                  onToday={goToToday}
                  onViewChange={changeCalendarView}
                />
              )}
              <div className="h-full min-h-0 motionlab-trainer-calendar">
                <FullCalendar
                  ref={calendarRef}
                  key={`${calendarView}-${visibleDays}`}
                  plugins={[timeGridPlugin, interactionPlugin]}
                  locale={deLocale}
                  initialView={calendarView}
                  duration={visibleDays !== 7 ? { days: visibleDays } : undefined}
                  scrollTime={TRAINER_CALENDAR_DEFAULT_SCROLL_TIME}
                  scrollTimeReset={false}
                  headerToolbar={showMobileToolbar
                    ? false
                    : {
                      left: 'prev,next today',
                      center: 'title',
                      right: '',
                    }}
                  buttonText={{
                    today: 'Heute',
                  }}
                  weekends={true}
                  nowIndicator={true}
                  allDaySlot={false}
                  slotMinTime={TRAINER_CALENDAR_START_TIME}
                  slotMaxTime={TRAINER_CALENDAR_END_TIME}
                  slotDuration={TRAINER_CALENDAR_SLOT_DURATION}
                  height="100%"
                  expandRows={true}
                  events={calendarEvents}
                  datesSet={(arg) => {
                    const from = arg.start.toISOString()
                    const to = arg.end.toISOString()
                    setCurrentCalendarView(arg.view.type as 'timeGridDay' | 'timeGridWeek')
                    setCalendarTitle(getCalendarToolbarTitle(arg))
                    setVisibleRange((prev) =>
                      prev.from === from && prev.to === to ? prev : { from, to },
                    )
                  }}
                  eventClick={(info) => {
                    const session = sessions.find((s) => s.id === info.event.id)
                    if (session) handleSessionClick(session)
                  }}
                  eventContent={(arg) => {
                    const { booked, max, min, isCancelled, isPast, resourceName } = arg.event.extendedProps
                    const reached = min === 0 || booked >= min

                    return (
                      <div className="p-1 text-[10px] leading-tight cursor-pointer">
                        <div className="font-bold truncate">{arg.event.title}</div>
                        {resourceName && (
                          <div className="text-[9px] opacity-80 truncate">@{resourceName}</div>
                        )}
                        {!isCancelled && (
                          <div className="flex flex-col gap-0.5 mt-0.5">
                            <div className="flex items-center gap-1">
                              <UsersIcon className="h-2.5 w-2.5" />
                              <span>
                                {booked}/{max}
                              </span>
                            </div>
                            {!isPast && (
                              <div
                                className={`font-semibold uppercase ${reached ? 'text-green-200' : 'text-yellow-200'}`}
                              >
                                {reached ? 'Anzahl erreicht' : 'Warten auf Teilnehmer'}
                              </div>
                            )}
                            {isPast && (
                              <div className="font-semibold uppercase text-slate-200">
                                Ende
                              </div>
                            )}
                          </div>
                        )}
                        {isCancelled && <div className="font-bold uppercase text-[9px]">Storniert</div>}
                      </div>
                    )
                  }}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[90dvh] w-[calc(100%-1rem)] max-w-2xl overflow-x-hidden overflow-y-auto p-4 sm:w-full sm:p-6">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: selectedSession?.courses?.color }}
                />
                <DialogTitle>{selectedSession?.courses?.name}</DialogTitle>
              </div>

            </div>
            <DialogDescription asChild>
              <div className="space-y-1">
                <div>{selectedSession && formatDateTime(selectedSession.starts_at)}</div>
                <div className="text-xs">
                  Anmeldung möglich bis: {selectedSession?.registration_until ? formatDateTime(selectedSession.registration_until) : '-'}
                </div>
                <div className="text-xs">
                  Stornierung möglich bis: {selectedSession?.cancellation_until ? formatDateTime(selectedSession.cancellation_until) : '-'}
                </div>
                {selectedSession?.resource?.name && (
                  <div className="text-xs font-semibold flex items-center gap-1">
                    Ort: <Badge variant="outline" className="h-5 text-[10px] px-1.5">{selectedSession.resource.name}</Badge>
                  </div>
                )}
                {selectedSession?.is_cancelled && (
                  <div className="pt-2">
                    <Badge variant="destructive">
                      Storniert
                    </Badge>
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="participants" className="mt-2 min-w-0 w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="participants" className="flex items-center gap-2">
                <UsersIcon className="h-4 w-4" />
                Teilnehmer
              </TabsTrigger>
              <TabsTrigger value="reporting" className="flex items-center gap-2">
                <FileTextIcon className="h-4 w-4" />
                Bericht
              </TabsTrigger>
            </TabsList>

            <TabsContent value="participants" className="mt-4">
              <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  Anmeldungen ({confirmedBookings.length} / {selectedSession?.max_participants})
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-full gap-2 text-xs md:w-auto"
                  onClick={() => setIsMessageDialogOpen(true)}
                >
                  <MegaphoneIcon className="h-3.5 w-3.5" />
                  Nachricht an alle
                </Button>
              </div>

              {bookingsQuery.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : confirmedBookings.length > 0 ? (
                <>
                  <div className="space-y-3 md:hidden">
                    {confirmedBookings.map((booking: Booking) => (
                      <div
                        key={booking.id}
                        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2.5"
                      >
                        <p className="min-w-0 truncate font-medium">
                          {booking.full_name}
                        </p>
                        <Badge
                          variant={booking.status === 'confirmed' ? 'default' : 'destructive'}
                          className={
                            booking.status === 'confirmed'
                              ? 'bg-green-600 hover:bg-green-700'
                              : ''
                          }
                        >
                          {booking.status === 'confirmed' ? 'Bestätigt' : 'Storniert'}
                        </Badge>
                        <p className="min-w-0 truncate text-sm text-muted-foreground">
                          {booking.email}
                        </p>
                        {booking.booked_by_user_id ? (
                          <Button size="sm" variant="outline" className="h-7 px-2.5" asChild>
                            <Link to={`/trainer/users/${booking.booked_by_user_id}`}>
                              Details
                            </Link>
                          </Button>
                        ) : (
                          <span className="text-right text-xs text-muted-foreground">-</span>
                        )}
                      </div>
                    ))}
                  </div>

                  <Table containerClassName="hidden max-h-[400px] md:block">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {confirmedBookings.map((booking: Booking) => (
                        <TableRow key={booking.id}>
                          <TableCell className="font-medium">{booking.full_name}</TableCell>
                          <TableCell className="text-muted-foreground">{booking.email}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                booking.status === 'confirmed'
                                  ? 'default'
                                  : booking.status === 'cancelled'
                                    ? 'destructive'
                                    : 'secondary'
                              }
                              className={
                                booking.status === 'confirmed'
                                  ? 'bg-green-600 hover:bg-green-700'
                                  : ''
                              }
                            >
                              {booking.status === 'confirmed'
                                ? 'Bestätigt'
                                : booking.status === 'cancelled'
                                  ? 'Storniert'
                                  : 'Unbekannt'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {booking.booked_by_user_id ? (
                              <Button size="sm" variant="outline" asChild>
                                <Link to={`/trainer/users/${booking.booked_by_user_id}`}>Details</Link>
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div className="py-12 text-center text-sm text-muted-foreground border rounded-md">
                  Noch keine Anmeldungen für diesen Kurs.
                </div>
              )}
            </TabsContent>

            <TabsContent value="reporting" className="mt-4 space-y-6">
              {selectedSession && new Date() < new Date(selectedSession.ends_at) ? (
                <Alert variant="destructive">
                  <AlertCircleIcon className="h-4 w-4" />
                  <AlertTitle>Bericht noch nicht verfügbar</AlertTitle>
                  <AlertDescription>
                    Sitzungsberichte können erst erstellt werden, nachdem die Sitzung erfolgt ist.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="grid gap-2">
                      <Label htmlFor="minutes" className="flex items-center gap-2">
                        <ClockIcon className="h-4 w-4" />
                        Gehaltene Minuten
                        <span className="text-xs text-muted-foreground font-normal">
                          (Geplant: {selectedSession?.courses?.duration_minutes} min)
                        </span>
                      </Label>
                      <Input
                        id="minutes"
                        type="number"
                        placeholder="Eingeben..."
                        value={reportMinutes}
                        onChange={(e) => setReportMinutes(e.target.value)}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="notes" className="flex items-center gap-2">
                        <FileTextIcon className="h-4 w-4" />
                        Notiz (optional)
                      </Label>
                      <Textarea
                        id="notes"
                        placeholder="Anmerkungen zum Kursverlauf..."
                        value={reportNotes}
                        onChange={(e) => setReportNotes(e.target.value)}
                        className="min-h-[100px]"
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="substitute" className="flex items-center gap-2">
                        <UserPlusIcon className="h-4 w-4" />
                        Vertretung (optional)
                      </Label>
                      <Select
                        value={reportSubstituteId}
                        onValueChange={setReportSubstituteId}
                      >
                        <SelectTrigger id="substitute">
                          <SelectValue placeholder="Wähle einen Trainer..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Keine Vertretung</SelectItem>
                          {trainersQuery.data
                            ?.filter((t: Profile) => t.id !== trainerId)
                            .map((t: Profile) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.full_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      disabled={reportMutation.isPending}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleSaveReport}
                      disabled={reportMutation.isPending}
                    >
                      {reportMutation.isPending ? 'Speichert...' : 'Stunden speichern'}
                    </Button>
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      <SessionMessageDialog
        sessionId={selectedSession?.id ?? ""}
        sessionName={selectedSession?.courses?.name}
        open={isMessageDialogOpen}
        onOpenChange={setIsMessageDialogOpen}
      />
    </div>
  )
}
